import styled, { css } from 'styled-components';
import { TooltipType } from './TooltipType';

export const TooltipContent = styled.div<{
  tooltipSize?: number;
  showOnBottom?: boolean;
  mobileFixed?: { top?: number; bottom?: number; left: number; arrowLeft: number } | null;
}>`
  width: auto;
  background-color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  color: #fff;
  text-align: left;
  border-radius: 8px;
  padding: 8px 12px;
  transition: opacity 0.5s ease-in-out;
  animation-name: fadeInFromNone;
  animation-duration: 0.2s;
  animation-fill-mode: forwards;
  position: absolute;
  z-index: 21;
  bottom: 150%;
  white-space: initial;

  ${({ tooltipSize }) =>
    tooltipSize &&
    css`
      width: ${tooltipSize}px;
    `}

  ${({ showOnBottom }) =>
    showOnBottom &&
    css`
      bottom: unset;
      top: 150%;
    `}

  &::after {
    content: ' ';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary} transparent transparent transparent;

    ${({ showOnBottom }) =>
      showOnBottom &&
      css`
        bottom: 100%;
        top: unset;
        border-color: transparent transparent ${({ theme }) => theme.kallisto.color.text.static.textPrimary} transparent;
      `}
  }

  ${({ mobileFixed, tooltipSize }) =>
    mobileFixed &&
    css`
      position: fixed;
      left: ${mobileFixed.left}px;
      transform: none;
      width: calc(100vw - 32px);
      ${tooltipSize ? `max-width: ${tooltipSize}px;` : ''}
      top: unset;
      bottom: unset;
      ${mobileFixed.top !== undefined ? `top: ${mobileFixed.top}px;` : ''}
      ${mobileFixed.bottom !== undefined ? `bottom: ${mobileFixed.bottom}px;` : ''}

      &::after {
        left: ${mobileFixed.arrowLeft}px;
        margin-left: 0;
      }
    `}
`;

const positionTooltip = (tooltipSize: number, position?: 'center' | 'left' | 'right') => {
  switch (position) {
    case 'left':
      return css`
        left: -4px;
        right: unset;

        &:after {
          left: 16px;
          right: unset;
        }
      `;

    case 'right':
      return css`
        left: unset;
        right: -14px;

        &:after {
          right: 16px;
          left: unset;
        }
      `;

    case 'center':
    default:
      return css`
        right: unset;
        left: -${tooltipSize / 2 - 14}px;
      `;
  }
};

export const TooltipAnchor = styled.span`
  position: relative;
  display: inline;
  color: inherit;
  font: inherit;
  text-decoration: inherit;
`;

/**
 * Holds the ref the mobile positioning measures, since the icon component cannot take one. An `<i>`
 * rather than a `<span>`: badges nudge every `span` they contain.
 */
export const TooltipIconAnchor = styled.i`
  display: flex;
`;

export const IconWithTooltip = styled.span<{
  tooltipSize: number;
  tooltipType: TooltipType;
  position?: 'left' | 'right' | 'center';
  isMobile?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 4px;

  img.icon-right {
    margin: 0;
  }

  svg,
  [data-library-icon] {
    margin-left: 4px;
    margin-right: 0;
    ${({ tooltipType }) =>
      tooltipType === TooltipType.gray400 &&
      css`
        fill: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
        color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
      `};

    ${({ tooltipType }) =>
      tooltipType === TooltipType.gray200 &&
      css`
        fill: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
        color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
      `};

    ${({ tooltipType }) =>
      tooltipType === TooltipType.gray800 &&
      css`
        fill: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
        color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
      `};

    ${({ tooltipType }) =>
      tooltipType === TooltipType.primary &&
      css`
        fill: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
        color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      `};
  }
  .tooltiptext {
    ${({ tooltipSize, position, isMobile }) =>
      !isMobile &&
      css`
        ${tooltipSize && `left: -${tooltipSize / 2 - 14}px;`}
        ${positionTooltip(tooltipSize, position)}
      `}
  }
`;
